import type { TrustInfo } from "@/data/portal/intelligence";
import { cn } from "@/lib/utils";
import {
  User, Camera, CheckCircle, FileText, Shield, Clock,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface Props {
  trust: TrustInfo;
}

export function TrustLayer({ trust }: Props) {
  const hasContent = trust.teamAssigned.length > 0 || trust.proofOfWork.length > 0 || trust.documents.length > 0 || trust.warrantyInfo;
  if (!hasContent) return null;

  return (
    <div className="space-y-4">
      {/* Team */}
      {trust.teamAssigned.length > 0 && (
        <div className="space-y-2">
          <SectionLabel icon={User} label="Your Team" />
          <div className="space-y-1.5">
            {trust.teamAssigned.map((member, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl bg-muted/30">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{member.name}</p>
                  <p className="text-[11px] text-muted-foreground">{member.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Proof of Work */}
      {trust.proofOfWork.length > 0 && (
        <div className="space-y-2">
          <SectionLabel icon={Camera} label="Proof of Work" />
          {trust.proofOfWork.map((item, i) => (
            <div key={i} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-success/5 border border-success/10">
              {item.type === "qa" ? (
                <CheckCircle className="h-4 w-4 text-success shrink-0" />
              ) : (
                <Camera className="h-4 w-4 text-success shrink-0" />
              )}
              <p className="text-sm text-foreground">{item.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Documents */}
      {trust.documents.length > 0 && (
        <div className="space-y-2">
          <SectionLabel icon={FileText} label="Documents" />
          {trust.documents.map((doc, i) => (
            <div key={i} className="flex items-center justify-between gap-3 p-2.5 rounded-xl bg-muted/30">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <p className="text-sm text-foreground truncate">{doc.name}</p>
              </div>
              <Badge variant="outline" className="text-[10px] shrink-0">{doc.status}</Badge>
            </div>
          ))}
        </div>
      )}

      {/* Warranty */}
      {trust.warrantyInfo && (
        <div className="space-y-2">
          <SectionLabel icon={Shield} label="Warranty" />
          <div className="p-3 rounded-xl bg-primary/5 border border-primary/10 space-y-1">
            <p className="text-sm font-medium text-foreground">{trust.warrantyInfo.type}</p>
            <p className="text-xs text-muted-foreground">
              Duration: {trust.warrantyInfo.duration}
            </p>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>Expires: {new Date(trust.warrantyInfo.expires).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionLabel({ icon: Icon, label }: { icon: typeof User; label: string }) {
  return (
    <p className="text-xs font-medium tracking-wide uppercase text-muted-foreground flex items-center gap-1.5">
      <Icon className="h-3 w-3" /> {label}
    </p>
  );
}
